import { type ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import {
  type FallbackProps,
  reportCaughtError,
  toFallbackProps,
} from './caughtError';
import { RootErrorFallback } from './RootErrorFallback';

export type RootErrorBoundaryProps = {
  children: ReactNode;
  /**
   * Values that reset the boundary when they change (`react-error-boundary`
   * `resetKeys`). For Pages Router apps pass the current route — e.g.
   * `resetKeys={[router.asPath]}` — so a client-side navigation clears a
   * caught error instead of stranding the user on the fallback.
   */
  resetKeys?: unknown[];
  /** Custom fallback renderer. Defaults to {@link RootErrorFallback}. */
  fallbackRender?: (props: FallbackProps) => ReactNode;
};

/**
 * App-root error boundary for static-export (`output: 'export'`) Pages Router
 * apps.
 *
 * In a no-server SPA, render-phase errors fall through to Next's `_error`
 * page, which only receives `{ statusCode, title }` — never the error itself —
 * so they are reported to Sentry as the useless `Error: Error undefined` with
 * a framework-only stack. This boundary catches them client-side and reports
 * the real error plus its React component stack via {@link reportRenderError}.
 *
 * Place it high in the app subtree, around page content. Pass `resetKeys` so
 * it recovers on navigation; the default fallback offers a full reload.
 */
export function RootErrorBoundary({
  children,
  resetKeys,
  fallbackRender,
}: RootErrorBoundaryProps) {
  return (
    <ErrorBoundary
      onError={reportCaughtError}
      resetKeys={resetKeys}
      fallbackRender={(caught) => {
        const props = toFallbackProps(caught);
        return fallbackRender ? (
          fallbackRender(props)
        ) : (
          <RootErrorFallback {...props} />
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
